import { useAuth, type AccountStatus } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { lazy, Suspense } from "react";
import { Clock, Ban, XCircle, RefreshCw, LogOut } from "lucide-react";
import { PortalLoader } from "@/components/portal-loader";
import { motion } from "framer-motion";
import { brand } from "@/lib/brand";

const ParentDashboard = lazy(() => import("@/features/parent-dashboard").then(m => ({ default: m.ParentDashboard })));
const StudentDashboard = lazy(() => import("@/features/student-dashboard").then(m => ({ default: m.StudentDashboard })));
const SchoolAdminDashboard = lazy(() => import("@/features/school-admin-dashboard").then(m => ({ default: m.SchoolAdminDashboard })));
const MerchantDashboard = lazy(() => import("@/features/merchant-dashboard").then(m => ({ default: m.MerchantDashboard })));
const SuperadminDashboard = lazy(() => import("@/features/superadmin-dashboard").then(m => ({ default: m.SuperadminDashboard })));
const FinanceOfficerDashboard = lazy(() => import("@/features/finance-officer-dashboard").then(m => ({ default: m.FinanceOfficerDashboard })));

const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

export function AppPage() {
  const { user, profile, loading, signOut } = useAuth();

  if (loading) {
    return <PortalLoader label="Preparing your workspace" />;
  }

  if (!user) {
    if (typeof window !== "undefined") {
      window.location.replace("/auth");
    }
    return <PortalLoader label="Redirecting to sign in" />;
  }

  const role = profile?.role ?? "parent";
  const status = profile?.account_status ?? null;

  // Superadmins always bypass approval — they are the approvers
  if (role !== "superadmin" && status && status !== "approved") {
    return <AccountStatusScreen status={status} onSignOut={signOut} />;
  }

  const dashboard = (
    <Suspense fallback={<PortalLoader label="Preparing your workspace" />}>
      {role === "student" && <StudentDashboard />}
      {role === "school_admin" && <SchoolAdminDashboard />}
      {role === "merchant" && <MerchantDashboard />}
      {role === "superadmin" && <SuperadminDashboard />}
      {role === "finance_officer" && <FinanceOfficerDashboard />}
      {role === "parent" && <ParentDashboard />}
    </Suspense>
  );
  return dashboard;
}

function AccountStatusScreen({ status, onSignOut }: { status: AccountStatus; onSignOut: () => Promise<void> }) {
  const config = {
    pending: {
      icon: Clock,
      title: "Your account is awaiting approval.",
      subtitle: "Your registration has been received and is currently being reviewed by a Superadmin. You'll be able to access your workspace once your account has been approved.",
      color: "#D97706",
      bg: "rgba(217, 119, 6, 0.06)",
      steps: [
        { label: "Registration submitted", done: true },
        { label: "Admin review", done: false, active: true },
        { label: "Account approved", done: false },
        { label: "Portal access", done: false },
      ],
    },
    rejected: {
      icon: XCircle,
      title: "Your account was not approved.",
      subtitle: "Your registration requires additional verification. Please contact your administrator for more information.",
      color: "#DC2626",
      bg: "rgba(220, 38, 38, 0.06)",
      steps: [
        { label: "Registration submitted", done: true },
        { label: "Admin review", done: true },
        { label: "Account rejected", done: true, active: true },
        { label: "Portal access", done: false },
      ],
    },
    suspended: {
      icon: Ban,
      title: "Your account is temporarily suspended.",
      subtitle: "Your account has been suspended by an administrator. Please contact support if you believe this is an error.",
      color: "#7C2D12",
      bg: "rgba(124, 45, 18, 0.06)",
      steps: [
        { label: "Registration submitted", done: true },
        { label: "Account approved", done: true },
        { label: "Account suspended", done: true, active: true },
        { label: "Portal access", done: false },
      ],
    },
  }[status];

  const Icon = config.icon;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4" style={{ background: "#FAFAF8", fontFamily: FS }}>
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 50% 0%, ${config.bg}, transparent 70%)` }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-lg"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: config.color }}>
            <Icon className="h-7 w-7 text-white" />
          </div>

          <h1 className="mb-3 text-2xl font-bold tracking-tight" style={{ color: "#1A1A1A" }}>
            {config.title}
          </h1>
          <p className="mb-8 max-w-sm text-sm leading-relaxed" style={{ color: "#666" }}>
            {config.subtitle}
          </p>

          {/* Progress journey */}
          <div className="mb-8 w-full max-w-sm space-y-1">
            {config.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold"
                    style={{
                      background: step.done ? config.color : step.active ? `${config.color}20` : "#F0F0F0",
                      color: step.done ? "white" : step.active ? config.color : "#999",
                    }}
                  >
                    {step.done ? "✓" : i + 1}
                  </div>
                  {i < config.steps.length - 1 && (
                    <div className="h-6 w-px" style={{ background: step.done ? config.color : "#E0E0E0" }} />
                  )}
                </div>
                <span
                  className="text-sm font-medium"
                  style={{ color: step.done || step.active ? "#1A1A1A" : "#999" }}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="gap-1.5"
              style={{ fontFamily: FS }}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Check Status
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSignOut()}
              className="gap-1.5"
              style={{ fontFamily: FS }}
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </Button>
          </div>

          <p className="mt-8 text-[11px]" style={{ color: "#999", fontFamily: FS }}>
            {brand.company} · {brand.city}, {brand.country}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
